/**
 * Find substrings inside a PDF.js text layer with fuzzy matching tuned
 * for credential extraction (CV / GCP / future doc validators).
 *
 * The text-layer search problem:
 * - Bedrock returns "Padmé Amidala" and "2023-06-20".
 * - The PDF's text layer has "Padmé Amidala" exactly, or "20-Jun-2023"
 *   for the date — never ISO.
 * - PDF.js gives us per-page text items with their bounding boxes in
 *   PDF user-space; concatenating items doesn't preserve spaces
 *   consistently (rendered glyph runs are split arbitrarily).
 *
 * Strategy:
 * 1. Normalize both haystack and needle (lowercase, strip diacritics,
 *    collapse whitespace, strip non-alphanumeric for non-date fields).
 * 2. For dates: try a handful of common print formats derived from the
 *    ISO date until one hits.
 * 3. Build a flat "normalized stream" with a parallel mapping back to
 *    the original text items so we can recover which items make up the
 *    matched substring.
 */

export interface PdfTextItem {
  /** Original string fragment as rendered. */
  str: string;
  /** Bounding box in PDF user-space (origin bottom-left). */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MatchedBox {
  /** Union bounding box of all items that made up the match. PDF
   * user-space. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Page index the match was found on (0-based). */
  pageIndex: number;
}

/** Strip diacritics, lowercase, collapse whitespace. Used as a
 * canonical form for name comparison. Dates get a separate path. */
export function normalize(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Given an ISO date "YYYY-MM-DD", return candidate strings that
 * common cert issuers print. We try the most specific first. */
export function dateCandidates(iso: string): string[] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return [iso];
  const [, yyyy, mm, dd] = m;
  const monthNum = parseInt(mm, 10);
  const dayNum = parseInt(dd, 10);
  const monthLong = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ][monthNum - 1];
  const monthShort = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ][monthNum - 1];
  return [
    `${dd}-${monthShort}-${yyyy}`,        // 20-Jun-2023 (CITI)
    `${dd} ${monthShort} ${yyyy}`,        // 20 Jun 2023
    `${monthShort} ${dayNum}, ${yyyy}`,   // Jun 20, 2023
    `${monthLong} ${dayNum}, ${yyyy}`,    // June 20, 2023
    `${dayNum} ${monthShort} ${yyyy}`,    // 20 Jun 2023
    `${dayNum} ${monthLong} ${yyyy}`,     // 20 June 2023
    `${mm}/${dd}/${yyyy}`,                // 06/20/2023
    `${dd}/${mm}/${yyyy}`,                // 20/06/2023
    iso,                                  // 2023-06-20
  ];
}

/** Find the first occurrence of ``needle`` in the page's text items.
 * Returns a single union bounding box covering all matched items, or
 * null if no match. For dates, callers pass the result of
 * dateCandidates() one at a time and take the first hit. */
export function findInPage(
  items: PdfTextItem[],
  needle: string,
): { box: Omit<MatchedBox, "pageIndex">; matchedRange: [number, number] } | null {
  if (!needle.trim()) return null;
  const target = normalize(needle);
  if (!target) return null;

  // Build a flat normalized stream with a mapping from each character
  // in the stream back to the source item index. Spaces are inserted
  // between items to mirror what normalize() would do for the joined
  // string, so item boundaries don't accidentally merge into runs.
  const charToItem: number[] = [];
  let stream = "";
  for (let i = 0; i < items.length; i++) {
    const piece = normalize(items[i].str);
    if (i > 0 && stream.length > 0 && !stream.endsWith(" ")) {
      stream += " ";
      charToItem.push(-1);
    }
    for (const ch of piece) {
      stream += ch;
      charToItem.push(i);
    }
  }

  const start = stream.indexOf(target);
  if (start < 0) return null;
  const end = start + target.length;

  // Recover which items made up the match, then take the union of
  // their bounding boxes.
  const matchedItems = new Set<number>();
  for (let i = start; i < end; i++) {
    const idx = charToItem[i];
    if (idx >= 0) matchedItems.add(idx);
  }
  if (matchedItems.size === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const idx of matchedItems) {
    const it = items[idx];
    minX = Math.min(minX, it.x);
    minY = Math.min(minY, it.y);
    maxX = Math.max(maxX, it.x + it.width);
    maxY = Math.max(maxY, it.y + it.height);
  }
  return {
    box: {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    },
    matchedRange: [start, end],
  };
}
